import React from 'react';
import { Theme, TextField, MenuItem, Grid, IconButton, useTheme, Box } from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { FixedTimesDayType, FixedTimesObject } from 'common/types';
import moment, { Moment } from 'moment-timezone';
import TimePicker from 'components/TimePicker';
import DatePicker from 'components/DatePicker';
import { useTranslation } from 'services/localization/useTranslation';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { isFixedTimeOutsideOpeningHours } from 'common/utils/shopUtils';
import { RiErrorWarningLine, RiCloseLine } from 'react-icons/ri';
import CustomTooltip from './CustomTooltip';
import { getDayTypeTranslation } from 'common/utils/translations';
interface OwnProps {
	value: FixedTimesObject;
	index: number;
	onChange: (value: FixedTimesObject) => void;
	onRemove: () => void;
	disabled?: boolean;
	showOpeningHoursWarning?: boolean;
}

type Props = OwnProps;
const FixedStartTimeRow = (props: Props) => {
	const classes = useStyles();
	const { index, value, showOpeningHoursWarning } = props;
	const { dayType, time, customDate } = value;
	const theme = useTheme();
	const { t } = useTranslation();
	const openingHours = useSelector(ShopSelectors.activeLocationOpeningHoursWithException);

	const menuItemsCollection: FixedTimesDayType[] = [
		'allDays',
		'weekDays',
		'weekends',
		'monday',
		'tuesday',
		'wednesday',
		'thursday',
		'friday',
		'saturday',
		'sunday',
		'custom',
	];

	const isOutsideOpeningHours = isFixedTimeOutsideOpeningHours(openingHours, value);

	const handleDayTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newDayType = e.target.value as FixedTimesDayType;
		let newFixedTimesObject: FixedTimesObject = {
			...props.value,
			dayType: newDayType,
		};

		if (newDayType === 'custom') {
			newFixedTimesObject.customDate = moment().toISOString();
		} else {
			delete newFixedTimesObject.customDate;
		}
		props.onChange(newFixedTimesObject);
	};

	const handleFixedTimesChange = (value: string) => {
		const newFixedTimesObject: FixedTimesObject = {
			...props.value,
			time: value,
		};
		props.onChange(newFixedTimesObject);
	};

	const handleCustomDateChange = (value: Moment | null) => {
		const newFixedTimesObject: FixedTimesObject = {
			...props.value,
			customDate: value?.toISOString() ?? moment().toISOString(), //Let's not allow the date to be cleared
		};

		props.onChange(newFixedTimesObject);
	};

	const handleRemove = () => {
		props.onRemove();
	};

	const renderMenuItems = () => {
		return menuItemsCollection.map((dayTypeItem) => {
			return (
				<MenuItem key={dayTypeItem} value={dayTypeItem}>
					{getDayTypeTranslation(dayTypeItem, t)}
				</MenuItem>
			);
		});
	};

	return (
		<Box className={classes.root}>
			<Grid container spacing={1}>
				<Grid item xs={12} md={5}>
					<TextField
						fullWidth
						select
						id="day-type"
						name="dayType"
						color="primary"
						variant="outlined"
						value={dayType}
						onChange={handleDayTypeChange}
						SelectProps={{
							classes: {
								select: classes.select,
							},
						}}
						disabled={props.disabled}
					>
						{renderMenuItems()}
					</TextField>
				</Grid>
				{dayType === 'custom' && (
					<Grid item xs={6} md={3}>
						<DatePicker
							value={moment(customDate)}
							onChange={handleCustomDateChange}
							id="fixed-start-time-custom-date"
							styleProps={{
								height: 35,
								bordered: true,
							}}
							singleDatePickerProps={{
								showDefaultInputIcon: true,
							}}
							disabled={props.disabled}
						/>
					</Grid>
				)}
				<Grid item xs={5} md={3}>
					<Box display="flex" flexDirection="row" alignItems="center">
						<TimePicker
							onChange={handleFixedTimesChange}
							selectedDate={moment(time, 'HH:mm').toISOString()}
							noMinDate={true}
							outlined={true}
							disabled={props.disabled}
						/>
					</Box>
				</Grid>
				{isOutsideOpeningHours && showOpeningHoursWarning && (
					<Grid item xs={1}>
						<Box height="100%" mr={1} display="flex" flexDirection="row" alignItems="center">
							<CustomTooltip
								title={t(
									'products.fixedStartTimeOutsideHoursWarning',
									"This start time is outside store opening hours, and will not be shown to your customers. If you want, you can enable 'Ignore store opening hours' below to show it anyway.",
								)}
							>
								<IconButton size="small">
									<RiErrorWarningLine color={theme.palette.warning.main} />
								</IconButton>
							</CustomTooltip>
						</Box>
					</Grid>
				)}
			</Grid>
			<Box className={classes.deleteButton}>
				<IconButton
					onClick={handleRemove}
					size="small"
					disabled={props.disabled || index === 0}
					className={props.disabled || index === 0 ? classes.invisible : undefined}
				>
					<RiCloseLine />
				</IconButton>
			</Box>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			width: '100%',
			borderBottom: `1px solid ${theme.palette.divider}`,
			display: 'flex',
			flexDirection: 'row',
			padding: theme.spacing(2, 0),
		},
		deleteButton: {
			marginLeft: theme.spacing(2),
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
		},
		select: {
			padding: 10,
		},
		invisible: {
			visibility: 'hidden',
		},
	}),
);

export default FixedStartTimeRow;
